import React, { useCallback, useEffect, useRef } from 'react'
import { Box, CircularProgress, Paper, Theme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'
import { chatPageSize, IChatMessage } from '../../../../../../api/types/chat'
import cx from 'classnames'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  InfiniteLoader,
  List,
} from 'react-virtualized'
import { ChatMessageEntry } from '../ChatMessageEntry/ChatMessageEntry'
import { CellMeasurerChildProps } from 'react-virtualized/dist/es/CellMeasurer'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chatContainer: {
      flex: '1 1 auto',
      paddingLeft: 4,
      paddingRight: 4,
      paddingBottom: 4,
      overflow: 'auto',
      marginBottom: '4px',
    },
  })
)

interface Props {
  chatRef: React.Ref<any>
  messages: IChatMessage[]
  isLoading: boolean
  loadMore: () => Promise<any>
}

export const ChatHistory: React.FC<Props> = ({ chatRef, messages, isLoading, loadMore }) => {
  const classes = useStyles()
  const listRef = useRef<any>()
  const cache = new CellMeasurerCache({
    defaultHeight: 50,
    fixedWidth: true,
    keyMapper: rowIndex => messages[rowIndex].id,
  })

  useEffect(() => {
    if (listRef?.current) {
      listRef.current.scrollToRow(messages.length - 1)
    }
  }, [messages.length])

  const isRowLoaded = useCallback(
    ({ index }: Index) => {
      return !!messages[index]
    },
    [messages.length]
  )

  function rowRenderer(props: any) {
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={props.key}
        parent={props.parent}
        rowIndex={props.index}
      >
        {({ registerChild, measure }: CellMeasurerChildProps) => {
          const loaded = isRowLoaded({ index: props.index })
          return (
            <>
              {!loaded && (
                <Box ref={registerChild} style={props.style}>
                  Loading
                </Box>
              )}
              {loaded && (
                <Box ref={registerChild} style={props.style}>
                  <ChatMessageEntry
                    key={props.key}
                    entry={messages[props.index]}
                    onImageLoad={() => {
                      measure()
                    }}
                  />
                </Box>
              )}
            </>
          )
        }}
      </CellMeasurer>
    )
  }

  const handleRowsRendered = (startIndex: number, stopIndex: number) => {
    if (!isLoading && startIndex === 0) {
      loadMore().then(() => listRef.current.scrollToRow(chatPageSize + startIndex))
    }
  }

  return (
    <>
      <Paper className={cx(classes.chatContainer, 'chat')} ref={chatRef}>
        <InfiniteLoader
          isRowLoaded={isRowLoaded}
          loadMoreRows={() => Promise.resolve()}
          rowCount={messages.length}
        >
          {({ onRowsRendered, registerChild }) => (
            <AutoSizer>
              {({ width, height }) => {
                registerChild(listRef)
                return (
                  <>
                    {isLoading && (
                      <div
                        style={{
                          position: 'fixed',
                          left: width / 2,
                        }}
                      >
                        <CircularProgress />
                      </div>
                    )}
                    <List
                      ref={listRef}
                      onRowsRendered={({ stopIndex, startIndex }) => {
                        handleRowsRendered(startIndex, stopIndex)
                        onRowsRendered({
                          startIndex,
                          stopIndex,
                        })
                      }}
                      deferredMeasurementCache={cache}
                      height={height}
                      rowCount={messages.length}
                      rowHeight={cache.rowHeight}
                      rowRenderer={rowRenderer}
                      width={width}
                      overscanRowCount={4}
                    />
                  </>
                )
              }}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </Paper>
    </>
  )
}
